import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { SchemaOrg, ArticleSchema } from '@/components/base/SchemaOrg';
import { movies } from '@/mocks/movies';
import { categoryNameMap } from '@/mocks/categories';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === id);

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-8 min-h-screen flex flex-col items-center justify-center text-foreground-600">
          <i className="ri-emotion-sad-line text-5xl mb-4" />
          <p className="text-lg">影片未找到</p>
          <Link to="/" className="mt-4 text-primary-500 hover:text-primary-400 transition-colors">
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName = categoryNameMap[movie.category] || movie.type;
  const relatedMovies = movies
    .filter((m) => m.id !== movie.id && (m.category === movie.category || m.genres.some((g) => movie.genres.includes(g))))
    .slice(0, 6);

  return (
    <>
      <SchemaOrg
        type="WebPage"
        pageTitle={`${movie.title} - ${movie.type}在线观看 - 八戒影院`}
        pageDescription={movie.description}
        pageUrl={`/detail/${movie.id}`}
        datePublished={movie.releaseDate}
      />
      <ArticleSchema
        title={`${movie.title} - ${movie.type}高清在线观看`}
        description={movie.description}
        url={`/detail/${movie.id}`}
        imageUrl={movie.poster}
        datePublished={movie.releaseDate}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 md:pt-24 pb-8">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: categoryName, href: `/category/${movie.category}` },
            { label: movie.title },
          ]} />
        </div>

        <article className="w-full px-4 md:px-6 mt-6">
          <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
            <div className="flex-shrink-0 w-full sm:w-[280px] md:w-[320px] mx-auto lg:mx-0">
              <div className="rounded-lg overflow-hidden aspect-[2/3] bg-background-200">
                <img
                  src={movie.poster}
                  alt={movie.title}
                  title={`${movie.title} - 八戒影院高清在线观看`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {movie.title}
              </h1>
              <p className="mt-1 text-sm text-foreground-500">{movie.originalTitle}</p>

              <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
                <span className="flex items-center gap-1 bg-primary-500/10 text-primary-400 px-2 py-1 rounded-md">
                  <i className="ri-star-fill text-xs" />
                  <strong>{movie.rating}</strong>
                </span>
                <span>{movie.year}</span>
                <span>{movie.type}</span>
                <span>{movie.region}</span>
                <span>{movie.duration}</span>
                <span className="text-accent-400 font-medium">{movie.status}</span>
              </div>

              <div className="mt-4 flex flex-wrap gap-1.5">
                {movie.genres.map((genre) => (
                  <span key={genre} className="px-3 py-1 rounded-full text-xs bg-secondary-500/20 text-secondary-300 whitespace-nowrap">
                    {genre}
                  </span>
                ))}
              </div>

              <div className="mt-5 space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <span className="text-foreground-500 whitespace-nowrap min-w-[60px]">导演：</span>
                  <span className="text-foreground-700">{movie.director}</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-foreground-500 whitespace-nowrap min-w-[60px]">主演：</span>
                  <span className="text-foreground-700">{movie.cast.join(' / ')}</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-foreground-500 whitespace-nowrap min-w-[60px]">集数：</span>
                  <span className="text-foreground-700">{movie.episodes}</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-foreground-500 whitespace-nowrap min-w-[60px]">语言：</span>
                  <span className="text-foreground-700">{movie.language}</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-foreground-500 whitespace-nowrap min-w-[60px]">播放量：</span>
                  <span className="text-accent-400 font-semibold">{movie.playCount}</span>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button className="flex items-center justify-center gap-2 px-8 py-3 bg-primary-500 text-background-950 font-semibold rounded-full text-sm hover:bg-primary-400 transition-colors whitespace-nowrap">
                  <i className="ri-play-circle-fill text-lg" />
                  立即播放
                </button>
                <button className="flex items-center justify-center gap-2 px-8 py-3 border border-foreground-700/40 text-foreground-700 font-semibold rounded-full text-sm hover:border-primary-500 hover:text-primary-400 transition-colors whitespace-nowrap">
                  <i className="ri-heart-line text-base" />
                  收藏
                </button>
                <button className="flex items-center justify-center gap-2 px-8 py-3 border border-foreground-700/40 text-foreground-700 font-semibold rounded-full text-sm hover:border-primary-500 hover:text-primary-400 transition-colors whitespace-nowrap">
                  <i className="ri-share-forward-line text-base" />
                  分享
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 md:mt-10">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">
              剧情简介
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed max-w-3xl">
              {movie.description}
            </p>
          </div>

          {movie.tags.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-foreground-700 mb-2">标签</h3>
              <div className="flex flex-wrap gap-2">
                {movie.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/${movie.category}`}
                    className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 hover:text-primary-400 border border-background-300/40 transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>

        {relatedMovies.length > 0 && (
          <section className="w-full px-4 md:px-6 mt-10 md:mt-14">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4" data-product-shop>
              {relatedMovies.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}